import React, { useEffect, useState, useCallback } from 'react';
import { opsApi, fmtNum, GRAD, FONT_HEAD, ROLE_LABEL, Pagination } from './opsApi';
import { Input } from '@/components/ui/input';
import { ScrollText, Search } from 'lucide-react';

export default function OpsAudit() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');

  const load = useCallback(() => {
    opsApi.get('/audit-logs', { params: { page, page_size: 50, action: action || undefined } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [page, action]);

  useEffect(() => { const t = setTimeout(load, action ? 350 : 0); return () => clearTimeout(t); }, [load, action]);

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="ops-audit-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>Audit Log</h1>
        <p className="text-sm text-slate-500 mt-1">Append-only record of every admin action across the platform{data ? ` · ${fmtNum(data.total)} entries` : ''}</p>
      </div>

      <div className="relative w-72">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="audit-search" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}
          placeholder="Filter by action, e.g. invoice…" className="pl-9 h-9 bg-white" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {!data ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#34A853]" /></div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <ScrollText className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No audit entries match.</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
                  <th className="px-5 py-3 font-medium">When</th>
                  <th className="px-4 py-3 font-medium">Actor</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50">
                    <td className="px-5 py-3 text-xs text-slate-400 whitespace-nowrap">{fmtTime(a.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-medium">{a.actor_name}</div>
                      <div className="text-[10px] text-slate-400">{ROLE_LABEL[a.actor_role] || a.actor_role} · <span className="font-mono">{a.actor_scope}</span></div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-md text-[11px] font-mono text-white" style={{ background: GRAD }}>{a.action}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{a.detail || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination data={data} onPage={setPage} />
          </>
        )}
      </div>
    </div>
  );
}
